/**
 * useBirdMovements — query + mutation hooks for the Bird Movement Ledger.
 *
 * Hooks exported:
 *   useBirdMovements(filters?)        — list query with optional filters
 *   useCreateMovement()               — create a new movement
 *   useUpdateMovementStatus()         — approve / post / reject a movement
 *   useDeleteMovement()               — soft-delete / cancel a movement
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  fetchMovements,
  createMovement,
  updateMovementStatus,
  deleteMovement,
  type MovementsFilter,
} from '@/lib/birdRecordsApi';
import { queryKeys } from '@/lib/queryKeys';
import type { BirdMovement, BirdMovementStatus } from '@/app/bird-records/components/birdRecordsData';

// ─── List query ───────────────────────────────────────────────────────────────

export function useBirdMovements(
  filters?: MovementsFilter,
  options?: Omit<UseQueryOptions<BirdMovement[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<BirdMovement[]>({
    queryKey: queryKeys.movements.list(filters as Record<string, unknown>),
    queryFn: () => fetchMovements(filters),
    ...options,
  });
}

// ─── Create mutation ──────────────────────────────────────────────────────────

export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<BirdMovement, 'id' | 'movementNumber' | 'createdAt' | 'updatedAt'>) =>
      createMovement(data),
    onSuccess: () => {
      // Invalidate all movement list queries and flock population (population changes)
      qc.invalidateQueries({ queryKey: queryKeys.movements.all });
      qc.invalidateQueries({ queryKey: queryKeys.flockPopulation.all });
    },
  });
}

// ─── Update status mutation (approve / post / reject) ─────────────────────────

export function useUpdateMovementStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      status,
      updatedBy,
    }: {
      id: string;
      status: BirdMovementStatus;
      updatedBy?: string;
    }) => updateMovementStatus(id, status, updatedBy),

    // Optimistic update — immediately reflect the status change in the cache
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: queryKeys.movements.all });
      const previousData = qc.getQueriesData<BirdMovement[]>({
        queryKey: queryKeys.movements.all,
      });
      qc.setQueriesData<BirdMovement[]>(
        { queryKey: queryKeys.movements.all },
        (old) =>
          old?.map((m) =>
            m.id === id ? { ...m, status, updatedAt: new Date().toISOString() } : m,
          ),
      );
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      // Roll back on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.movements.all });
      qc.invalidateQueries({ queryKey: queryKeys.flockPopulation.all });
      // Also invalidate sale records because posting a movement may affect them
      qc.invalidateQueries({ queryKey: queryKeys.saleRecords.all });
    },
  });
}

// ─── Delete mutation ──────────────────────────────────────────────────────────

export function useDeleteMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMovement(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: queryKeys.movements.all });
      const previousData = qc.getQueriesData<BirdMovement[]>({
        queryKey: queryKeys.movements.all,
      });
      qc.setQueriesData<BirdMovement[]>(
        { queryKey: queryKeys.movements.all },
        (old) => old?.filter((m) => m.id !== id),
      );
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.movements.all });
      qc.invalidateQueries({ queryKey: queryKeys.flockPopulation.all });
    },
  });
}
